import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import useStore, { BRAND_COLORS } from '@/store/useStore';
import { apiGetStoreInfo, apiGuestLogin } from '@/services/api';
import useStoreContext from '@/hooks/useStoreContext';
import { storeRoute } from '@/lib/routes';
import PwaInstallPrompt from '@/components/guest/PwaInstallPrompt';

const LOGIN_MODE_KEY = 'flyback_guest_login_mode';
const ENTRY_MERCHANT_KEY = 'flyback_entry_merchant_id';
const ENTRY_MERCHANT_KEY_V2 = 'entry_merchant_id';
const COUNTRY_OPTIONS = [
  { code: '+1', label: '+1' },
  { code: '+91', label: '+91' },
];

function getInitialAuthMode() {
  if (typeof window === 'undefined') return 'new';
  return window.localStorage.getItem(LOGIN_MODE_KEY) === 'existing' ? 'existing' : 'new';
}

function setReturningUserMode() {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOGIN_MODE_KEY, 'existing');
}

function hexToRgbString(hex) {
  const safe = (hex || '').replace('#', '');
  if (safe.length !== 6) return '14, 122, 98';
  const n = Number.parseInt(safe, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

function resolveBrandPalette(color) {
  if (String(color || '').toLowerCase() === '#a5f2f3') return BRAND_COLORS.brand;
  if (String(color || '').toLowerCase() === '#5f9ea0') return BRAND_COLORS.brand;
  if (color && BRAND_COLORS[color]) return BRAND_COLORS[color];
  if (typeof color === 'string' && /^#[0-9a-fA-F]{6}$/.test(color)) {
    return { hex: color, start: color, end: color };
  }
  return BRAND_COLORS.brand;
}

function shadeHex(hex, factor) {
  const safe = (hex || '').replace('#', '');
  if (safe.length !== 6) return '#0E7A62';
  let r = parseInt(safe.slice(0, 2), 16);
  let g = parseInt(safe.slice(2, 4), 16);
  let b = parseInt(safe.slice(4, 6), 16);
  if (factor >= 0) {
    r = Math.round(r + (255 - r) * factor);
    g = Math.round(g + (255 - g) * factor);
    b = Math.round(b + (255 - b) * factor);
  } else {
    r = Math.round(r * (1 + factor));
    g = Math.round(g * (1 + factor));
    b = Math.round(b * (1 + factor));
  }
  return `#${Math.max(0, Math.min(255, r)).toString(16).padStart(2, '0')}${Math.max(0, Math.min(255, g)).toString(16).padStart(2, '0')}${Math.max(0, Math.min(255, b)).toString(16).padStart(2, '0')}`;
}

function normalizeLocalPhone(value) {
  return String(value || '').replace(/\D/g, '').slice(-10);
}

function toE164(countryCode, localPhone) {
  const digits = normalizeLocalPhone(localPhone);
  if (!digits) return '';
  return `${String(countryCode || '+1').trim()}${digits}`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const merchantId = useStoreContext();
  const { guestLogin, guestToken, guestUser, setCurrentMerchantId, setStoreInfo, storeInfo, guestTheme } = useStore();
  const activeStoreInfo = storeInfo?.id === merchantId ? storeInfo : null;

  const [authMode, setAuthMode] = useState(getInitialAuthMode);
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [anniversaryDate, setAnniversaryDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cleanPhone = useMemo(() => normalizeLocalPhone(phone), [phone]);
  const fullPhone = useMemo(() => toE164(countryCode, phone), [countryCode, phone]);
  const maxDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    setCurrentMerchantId(merchantId);
    setStoreInfo(null);
    let mounted = true;
    apiGetStoreInfo(merchantId)
      .then((info) => {
        if (mounted) setStoreInfo(info);
      })
      .catch(() => {});

    if (guestToken) {
      if (guestUser?.merchant_id === merchantId) {
        navigate(storeRoute(merchantId, 'wallet'), { replace: true });
      } else if (guestUser?.merchant_id && guestUser.merchant_id !== merchantId) {
        window.localStorage.setItem(ENTRY_MERCHANT_KEY, merchantId);
        window.localStorage.setItem(ENTRY_MERCHANT_KEY_V2, merchantId);
        navigate('/store', { replace: true });
      }
    }
    return () => {
      mounted = false;
    };
  }, [guestToken, guestUser, merchantId, navigate, setCurrentMerchantId, setStoreInfo]);

  useEffect(() => {
    const colorKey = activeStoreInfo?.brand_color;
    const palette = resolveBrandPalette(colorKey);
    const base = palette.hex;
    const start = palette.start || base;
    const end = palette.end || base;
    const appliedStart = guestTheme === 'dark' ? shadeHex(start, -0.28) : start;
    const appliedEnd = guestTheme === 'dark' ? shadeHex(end, -0.4) : end;
    const appliedSolid = guestTheme === 'dark' ? shadeHex(base, -0.34) : base;
    document.documentElement.style.setProperty('--brand-start', appliedStart);
    document.documentElement.style.setProperty('--brand-end', appliedEnd);
    document.documentElement.style.setProperty('--brand-solid', appliedSolid);
    document.documentElement.style.setProperty('--brand-rgb', hexToRgbString(appliedSolid));
  }, [activeStoreInfo?.brand_color, guestTheme]);

  const finishLogin = useCallback((result, fallbackStoreId) => {
    guestLogin(result.token, result.customer, result.merchant);
    setReturningUserMode();

    // PRIORITY 1: Redirect based on entry context (QR scan)
    const entryMerchantId =
      window.localStorage.getItem(ENTRY_MERCHANT_KEY) ||
      window.localStorage.getItem(ENTRY_MERCHANT_KEY_V2);
    if (entryMerchantId) {
      window.localStorage.removeItem(ENTRY_MERCHANT_KEY);
      window.localStorage.removeItem(ENTRY_MERCHANT_KEY_V2);
      navigate(storeRoute(entryMerchantId, 'wallet'), { replace: true });
      return;
    }

    // PRIORITY 2: Fallback to current store context
    const resolvedMerchantId = result?.merchant?.id || fallbackStoreId;
    if (resolvedMerchantId && resolvedMerchantId !== 'demo') {
      navigate(storeRoute(resolvedMerchantId, 'wallet'), { replace: true });
    } else {
      navigate('/store/select', { replace: true });
    }
  }, [guestLogin, navigate]);

  const handleNewUser = useCallback(async () => {
    const cleanName = name.trim();

    if (cleanName.length < 2) {
      setError('Please enter your full name.');
      return;
    }
    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await apiGuestLogin(fullPhone, '', cleanName, merchantId, {
        date_of_birth: dateOfBirth || null,
        anniversary_date: anniversaryDate || null,
      });
      finishLogin(result, merchantId);
    } catch (e) {
      const detail = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Unable to continue right now.';
      if (/otp|sign in/i.test(detail)) {
        setAuthMode('existing');
        setError('This phone number already has an account. Please sign in using OTP.');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  }, [anniversaryDate, cleanPhone, dateOfBirth, finishLogin, fullPhone, merchantId, name]);

  const handleExistingUser = useCallback(async () => {
    const cleanOtp = otp.trim();

    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (cleanOtp.length < 4) {
      setError('Please enter your OTP to sign in.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await apiGuestLogin(fullPhone, cleanOtp, '', merchantId);
      finishLogin(result, merchantId);
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Sign in failed.');
    } finally {
      setLoading(false);
    }
  }, [cleanPhone, finishLogin, fullPhone, merchantId, otp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (authMode === 'existing') {
      await handleExistingUser();
      return;
    }
    await handleNewUser();
  };

  const toggleMode = () => {
    setError('');
    setOtp('');
    setAuthMode((prev) => (prev === 'new' ? 'existing' : 'new'));
  };

  return (
    <div className={`${guestTheme === 'dark' ? 'dark ' : ''}min-h-screen flex flex-col items-center justify-center px-6 bg-[#faf5f5] dark:bg-background`} data-testid="login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-16 h-16 rounded-2xl gradient-brand flex items-center justify-center">
            <img src="/assets/flyback.png" alt="Flyback" className="w-12 h-12 object-contain" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              FlyBack Rewards
            </h1>
            <p className="text-[12px] text-muted-foreground">
              {activeStoreInfo ? `Sign in to ${activeStoreInfo.restaurant_name}` : 'Sign in to access your rewards.'}
            </p>
          </div>
        </div>

        <form className="bg-card rounded-2xl p-6 shadow-sm border border-border space-y-5" onSubmit={handleSubmit}>
          {authMode === 'new' && (
            <div className="space-y-2">
              <label className="text-sm font-semibold">Full Name</label>
              <div className="flex items-center h-12 rounded-xl border border-border bg-background overflow-hidden">
                <input
                  data-testid="name-input"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 h-full px-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-semibold">Phone Number</label>
            <div className="flex items-center h-12 rounded-xl border border-border bg-background overflow-hidden">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="h-full px-2.5 text-sm text-foreground border-r border-border bg-transparent outline-none"
                data-testid="country-code-select"
              >
                {COUNTRY_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                data-testid="phone-input"
                type="tel"
                placeholder="00000 00000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 h-full px-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                inputMode="numeric"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">Saved as: {fullPhone || `${countryCode}XXXXXXXXXX`}</p>
          </div>

          {authMode === 'new' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-semibold">Date of Birth (Optional)</label>
                <div className="flex items-center h-12 rounded-xl border border-border bg-background overflow-hidden">
                  <input
                    data-testid="dob-input"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    max={maxDate}
                    placeholder="Select your birth date"
                    className="flex-1 h-full px-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold">Anniversary (Optional)</label>
                <div className="flex items-center h-12 rounded-xl border border-border bg-background overflow-hidden">
                  <input
                    data-testid="anniversary-input"
                    type="date"
                    value={anniversaryDate}
                    onChange={(e) => setAnniversaryDate(e.target.value)}
                    max={maxDate}
                    placeholder="Select anniversary date"
                    className="flex-1 h-full px-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {authMode === 'existing' && (
            <div className="space-y-2">
              <label className="text-sm font-semibold">OTP</label>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="w-11 h-12 text-lg font-bold text-foreground rounded-xl border-2 bg-secondary/50 first:rounded-l-xl last:rounded-r-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
          )}

          {error && <p className="text-destructive text-sm text-center" data-testid="login-error">{error}</p>}

          <button
            type="submit"
            data-testid="verify-continue-btn"
            disabled={loading}
            className="w-full h-12 rounded-full gradient-brand-btn text-base font-semibold flex items-center justify-center"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : authMode === 'existing' ? 'Sign In' : 'Create Account'}
          </button>

          <button
            type="button"
            onClick={toggleMode}
            className="w-full text-sm font-semibold text-brand hover:opacity-85"
            data-testid="auth-mode-toggle"
          >
            {authMode === 'existing' ? 'New user? Create an account' : 'Existing user? Sign in with OTP'}
          </button>

          <div className="pt-2 text-center">
            <img src="/assets/flyback.png" alt="FlyBack" className="h-8 w-auto object-contain mx-auto" />
            <p className="text-xs text-muted-foreground mt-1 font-semibold">Powered by FlyBack</p>
          </div>
        </form>

        <p className="text-center text-xs text-muted-foreground px-4">
          By continuing, you agree to FlyBack&apos;s Terms of Service and Privacy Policy. Messaging rates may apply.
        </p>
      </motion.div>
      <PwaInstallPrompt />
    </div>
  );
}
